import { useEffect } from 'react';

const upsertMeta = (selector, attr, key, content) => {
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

/**
 * Imperatively manage document head for SEO (title, description, canonical, prev/next, OG).
 * Removes duplicates and cleans up prev/next when not provided.
 */
export const useSEO = ({ title, description, keywords, canonical, prev, next, ogType = 'website' } = {}) => {
  useEffect(() => {
    if (title) document.title = title;

    // De-duplicate any pre-existing description metas (e.g. static one in index.html)
    const descs = document.head.querySelectorAll('meta[name="description"]');
    descs.forEach((d, i) => { if (i > 0) d.remove(); });
    upsertMeta('meta[name="description"]', 'name', 'description', description);
    if (keywords) upsertMeta('meta[name="keywords"]', 'name', 'keywords', keywords);

    upsertLink('canonical', canonical);
    upsertLink('prev', prev);
    upsertLink('next', next);

    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', ogType);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
  }, [title, description, keywords, canonical, prev, next, ogType]);
};

/**
 * Inject (and clean up) a JSON-LD structured data block for the current page.
 */
export const useJsonLd = (id, data) => {
  const json = data ? JSON.stringify(data) : null;
  useEffect(() => {
    if (!json) return undefined;
    let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-jsonld', id);
      document.head.appendChild(el);
    }
    el.textContent = json;
    return () => { el.remove(); };
  }, [id, json]);
};

export default useSEO;
